import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import view from './PhotoLiveSelfiePage.view';
import {
    GetTokenChallenge, GetUserRestrictionVisitor,
    ValidateSelfie, ValidateVideoPhoto, VeridasServiceProcess
} from "../../../../reduxToolkit/Visitor/VisitorApi";
import { useNavigate, useParams } from "react-router-dom";
import logo from '../../../../assets/images/sklut.png';
import {
    setIsSuccessUserSelfie,
    setUserSelfie
} from "../../../../reduxToolkit/Visitor/VisitorSlice";
import FullPageLoader from '../../../../utils/FullPageLoader';
import { colors } from '../../../../enums/colors';
import useHandleBeforeUnload from '../../Functionality/useHandleBeforeUnload';
import useBackNavigation from '../../Functionality/useBackNavigation';
import i18next from 'i18next';
import { toast } from "react-toastify";
import {getFailedUrl, getSuccessUrl} from "../../Model/Models";
import {registerInEventVeridas} from "../../../Dashboard/Events/External/Model/Model";
import {useTranslation} from "react-i18next";

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * Component that handles:
 * - Loading VDAlive SDK scripts
 * - Capturing a live selfie or challenge video
 * - Validating identity with Veridas API
 * - Handling success/failure navigation
 */
const PhotoLiveSelfiePage = () => {
    // Extract parameters from route
    const { validationId, guardFlowId, userId, eventId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Theme colors for SDK customization
    const primaryColor = colors.primary;
    const general00 = colors.general00;
    const success650 = colors.success650;
    const accent475 = colors.accent475;

    // Redux state selectors
    const tokenChallenge = useSelector((state) => state.VisitorSlice.getTokenChallenge);
    const userRestrictionData = useSelector((state) => state.VisitorSlice.getUserRestrictionVisitor);
    const { getInvitationById } = useSelector((state) => state.InvitationSlice);

    // Local state for captured images and status
    const [prefaceImage, setPrefaceImage] = useState(null); // Initial still image
    const [faceImage, setFaceImage] = useState(null); // Liveness-verified image or video
    const [vtt, setVtt] = useState(null); // Video annotation file
    const [unmounted, setUnmounted] = useState(false); // SDK component status
    const [loading, setLoading] = useState(false); // Loading state

    // Refs to store DOM elements and SDK instance
    const targetRef = useRef(null);
    const vdAlive = useRef(null);
    const invitationId = getInvitationById?.invitation?.id;
    const failedUrl = getFailedUrl({ invitationId, guardFlowId, userId, eventId });
    const successUrl =  getSuccessUrl({ invitationId, guardFlowId, userId, eventId });
    const resolvedUserId  = userId ? userId: getInvitationById?.invitation?.visitorId;
    /**
     * Dynamically load VDAlive scripts and initialize SDK
     */
    const loadVDALIVE = async () => {
        try {
            // Remove old TFJS or face-api.js instances if wrong version loaded
            if (window.tf && window.tf.version && window.tf.version.tfjs.startsWith('1.')) {
                delete window.tf;
                if (window.faceapi) delete window.faceapi;
            }

            // Load scripts in sequence
            await loadScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@2.8.0/dist/tf.min.js');
            await loadScript('/js/opencv/cv_alive.js');
            await loadScript('/js/VDAlive.js');

            // Initialize SDK if available
            if (window.makeVDAliveWidget) {
                vdAlive.current = window.makeVDAliveWidget();
                sdkStart();
            }
        } catch (error) {}
    };

    /**
     * Helper to dynamically load a JS script
     */
    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            // Avoid duplicate loads
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = src.trim();
            script.onload = resolve;
            script.onerror = () => reject(new Error(i18next.t('something_went_wrong_2')));
            document.body.appendChild(script);
        });
    };

    /**
     * Configure and start VDAlive SDK
     */
    const sdkStart = () => {
        const jws = tokenChallenge || '';
        const gifPath = process.env.PUBLIC_URL + '/js/gifs/';
        let gif = jws !== '' ? 'VDAlive_Challenge.gif' : 'VDAlive_Smile.gif';

        try {
            if (!vdAlive.current) {
                vdAlive.current = window.makeVDAliveWidget();
            }

            vdAlive.current({
                targetSelector: '#target',
                pathModels: '/js/models/',
                infoAlertShow: true,
                infoModalShow: true,
                reviewImage: true,
                logEventsToConsole: true,
                ngas_images_path: gifPath,
                aliveChallenge: jws,
                infoUserAliveHeader: '',
                infoUserButtonTextColor: accent475,
                infoUserAliveMedia: gif,
                infoUserAliveHeaderColor: primaryColor,
                infoUserAliveTitleColor: primaryColor,
                infoUserAliveSubTitleColor: primaryColor,
                infoUserAliveColorButton: primaryColor,
                infoUserAliveNextButtonColor: primaryColor,
                infoUserAlivePrevButtonColor: primaryColor,
                stepsChallengeColor: primaryColor,
                buttonBackgroundColorDark: primaryColor,
                buttonBackgroundColorLight: primaryColor,
                buttonBackgroundColorDarkRepeat: 'transparent',
                buttonBackgroundColorLightRepeat: 'transparent',
                repeatButtonColor: primaryColor,
                sdkBackgroundColorInactive: general00,
                borderColorCenteringAidDetecting: success650,
                outerGlowCenteringAidDefault: primaryColor,
                detectionMessageBackgroundColor: primaryColor,
                detectionMessageTextColor: primaryColor,
                detectionMessageTextColorSelfie: primaryColor,
                confirmationDialogTextColor: primaryColor,
                confirmationColorTick: primaryColor,
                errorDisplayBackgroundColor: general00,
                errorDisplayTextColor: primaryColor,
                errorActionButtonBackgroundColor: general00,
                errorActionButtonTextColor: primaryColor,
                errorDisplayIconColor: primaryColor,
                customLogo: logo,
            });

        } catch (error) {
            toast.error(i18next.t('something_went_wrong_2'));
        }
    };

    /**
     * Get user restrictions on mount
     */
    useEffect(() => {
        dispatch(GetUserRestrictionVisitor()).then(({ payload: { data: { data } } }) => {
            if (data.livenessVisitor === true) {
                dispatch(GetTokenChallenge(validationId));
            }
        });
    }, []);

    /**
     * Attach SDK event listeners and load SDK when requirements met
     */
    useEffect(() => {
        const target = document.getElementById('target');
        if (!target || !userRestrictionData || Object.entries(userRestrictionData)?.length === 0) return;
        if (userRestrictionData.livenessVisitor === true && !tokenChallenge) return;

        target.addEventListener('VDAlive_mounted', onVDAliveMounted);
        target.addEventListener('VDAlive_mountFailure', onVDAliveMountFailure);
        target.addEventListener('VDAlive_unmounted', onVDAliveUnmounted);
        target.addEventListener('VDAlive_faceDetection', onVDAliveFaceDetection);

        loadVDALIVE();

        // Cleanup SDK on unmount
        return () => {
            handleDestroy();
        };
    }, [tokenChallenge, userRestrictionData]);

    /** Event: SDK mounted successfully */
    const onVDAliveMounted = () => setUnmounted(false);

    /** Event: SDK failed to mount */
    const onVDAliveMountFailure = () => {};

    /** Event: SDK unmounted */
    const onVDAliveUnmounted = () => setUnmounted(true);

    /** Event: Face detected, store captured images */
    const onVDAliveFaceDetection = (e) => {
        setPrefaceImage(e.detail.image);
        setFaceImage(e.detail.image_alive);
        setVtt(e.detail.webVTT);
    };

    /**
     * Once both images are available, stop camera and start validation
     */
    useEffect(() => {
        if (prefaceImage && faceImage) {
            destroyCameraAndSave();
        }
    }, [vtt, prefaceImage, faceImage, userRestrictionData?.livenessVisitor]);

    /** Destroy VDAlive camera widget */
    const handleDestroy = async () => {
        try {
            if (vdAlive.current && typeof vdAlive.current.destroy === 'function') {
                vdAlive.current.destroy();
            } else if (typeof window.destroyVDAliveWidget === 'function') {
                window.destroyVDAliveWidget();
            }
        } catch (e) {
            console.warn(t('vd_alive_cleanup_failed'), e);
        }
    };

    /** Handle failed validation, clean images, redirect */
    const handleFailed = async () => {
        setLoading(false);
        navigate(failedUrl);
    };

    /** Check validation status from Veridas API */
    const selfieValidationStatus = async (validationId) => {
        const { payload: { data: { data, success } } } = await dispatch(VeridasServiceProcess({ validationId, userId:resolvedUserId }));
        if (success) {
            dispatch(setUserSelfie(data));
            dispatch(setIsSuccessUserSelfie(success));
            if(eventId){
                registerInEventVeridas({
                    dispatch,
                    resolvedUserId,
                    eventId,
                    setLoading,
                    t,
                    navigate,
                    successUrl,
                    data,
                    validationId
                });
                return;
            }
            setLoading(false);
            navigate(successUrl);
        } else {
            await handleFailed();
        }
    };

    /** Send static selfie validation request */
    const sendValidateSelfie = async () => {
        if (validationId) {
            setLoading(true);
            const body = {
                image: prefaceImage,
                imageAlive: faceImage,
                validationId
            };
            const { payload: { data: { success } } } = await dispatch(ValidateSelfie(body));
            success ? await selfieValidationStatus(validationId) : handleFailed();
        }
    };

    /** Send liveness challenge video validation request */
    const sendValidateVideoPhoto = async () => {
        if (tokenChallenge && validationId) {
            setLoading(true);
            const body = {
                annotations: vtt,
                selfie: prefaceImage,
                video: faceImage,
                challenge: tokenChallenge,
                validationId,
                userId: resolvedUserId
            };
            const { payload: { data: { success } } } = await dispatch(ValidateVideoPhoto(body));
            success ? await selfieValidationStatus(validationId) : handleFailed();
        }
    };

    /** Stop camera and send correct validation request depending on restriction */
    const destroyCameraAndSave = async () => {
        await handleDestroy();
        if (vtt && userRestrictionData?.livenessVisitor === true) {
            await sendValidateVideoPhoto();
        } else if (userRestrictionData?.livenessVisitor === false) {
            await sendValidateSelfie();
        }
    };

    // Custom hooks to handle navigation and refresh behavior
    useBackNavigation({ getInvitationById, validationId });
    useHandleBeforeUnload({ getInvitationById, validationId });

    // Render loader or main view
    return loading ? <FullPageLoader /> :
        view({
            prefaceImage,
            faceImage,
            targetRef
        });
};

export default PhotoLiveSelfiePage;
